import React, { useEffect, useState, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { Box, xcss } from '@atlaskit/primitives';
import { SuggestRelatedIssuesModalContainer } from '@atlassian/jira-ai-related-issues/src/index.tsx';
import type { FormFieldValues } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import useIsAdminHubAIEnabled from '@atlassian/jira-platform-react-hooks-use-is-admin-hub-ai-enabled/src/controllers/index.tsx';
import { useProductName } from '@atlassian/jira-tenant-context-controller/src/components/product-name/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIssueCreateModalWithPrefillingForIssueCreateEmbed } from '@atlassian/jira-new-issue-create-trigger/src/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useOpenIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { JiraIssueRelatedIssuesContainer } from '@atlassian/jira-ai-related-issues/src/controllers/context.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getProductForAdminHubAi, transformTheIssueDataToGICFormat } from './utils.tsx';
import messages from './messages.tsx';

type Props = {
	getFormState: () => FormFieldValues;
	selectedProjectId?: string;
};
const getCustomRelatedIssueSearchPayload = (selectedProjectId: string, summary: string) => {
	return {
		project_id: selectedProjectId,
		issue_summary: summary,
	};
};

export const SimilarWorkItemInGIC = (props: Props) => {
	const summary = props.getFormState()?.values?.summary || '';
	const selectedProjectId = props.selectedProjectId || '';

	const [showComponent, setShowComponent] = useState(false);
	const [debouncedSummary, setDebouncedSummary] = useState('');
	const prevSummaryRef = useRef(summary);

	const productName = useProductName();
	const adminHubAiProductName = getProductForAdminHubAi(productName);

	const { data: isAdminHubAiEnabled, fetchAIEnablement } =
		useIsAdminHubAIEnabled(adminHubAiProductName);

	const getPrefilledIssueCreateProps = useIssueCreateModalWithPrefillingForIssueCreateEmbed();
	const openIssueCreateModal = useOpenIssueCreateModal();

	// check if AI is enabled for the selected project/product
	useEffect(() => {
		fetchAIEnablement();
	}, [fetchAIEnablement]);

	const debouncedUpdate = useMemo(
		() =>
			debounce((value) => {
				setDebouncedSummary(value);
				if (value) {
					setShowComponent(true);
				} else {
					setShowComponent(false);
				}
			}, 1000),
		[],
	);

	useEffect(() => {
		const trimmedSummary = String(summary).trim();
		const processedSummary = fg('gravity_similar_issues_in_gic_summary_clean_up')
			? trimmedSummary
			: summary;

		if (processedSummary !== prevSummaryRef.current) {
			debouncedUpdate(processedSummary);
			prevSummaryRef.current = processedSummary;
		}
		return () => {
			debouncedUpdate.cancel();
		};
	}, [summary, debouncedUpdate]);

	const customRelatedIssueSearchPayload = useMemo(() => {
		return getCustomRelatedIssueSearchPayload(selectedProjectId, debouncedSummary);
	}, [selectedProjectId, debouncedSummary]);

	const prefillIssueCreateModalWithData = async (transformedPayload: Record<string, unknown>) => {
		const { issueCreatePayload, prefilledCustomFields, ecosystemCallback } =
			await getPrefilledIssueCreateProps(transformedPayload);

		// Open the issue creation modal with the necessary payload
		openIssueCreateModal({
			triggerPointKey: 'global-create-button',
			payload: {
				...issueCreatePayload,
			},
			prefilledCustomFields,
			ecosystemCallback,
		});
	};

	const onCopyButtonClick = async (issueKey: string): Promise<void> => {
		try {
			// Fetch issue details from the API
			const response = await performGetRequest(`/rest/api/2/issue/${issueKey}`);

			// Transform the response fields into a usable payload
			const transformedPayload = transformTheIssueDataToGICFormat(response?.fields);

			// Get prefilled issue creation properties
			prefillIssueCreateModalWithData(transformedPayload);
		} catch (error: unknown) {
			// Log and fire error analytics
			fireErrorAnalytics({
				error: error instanceof Error ? error : new Error('Unknown error'),
				meta: {
					id: 'getIssueFieldsSimilarWorkItemsInGIC',
					packageName: '@atlassian/jira-issue-create',
					teamName: 'GRAVITY',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	return (
		showComponent &&
		isAdminHubAiEnabled && (
			<Box xcss={relatedIssuesContainerStyles}>
				<JiraIssueRelatedIssuesContainer scope="related-issues-in-gic">
					<SuggestRelatedIssuesModalContainer
						issueKey=""
						onIssueLinkSave={noop}
						existingIssues={['']}
						customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
						invokedFromSource="globalIssueCreate"
						headerTitle={messages.similarWorkItems}
						{...(expVal('similar_work_items_copy_field_milestone-2', 'isEnabled', false)
							? { onCopyButtonClick }
							: undefined)}
					/>
				</JiraIssueRelatedIssuesContainer>
			</Box>
		)
	);
};
const relatedIssuesContainerStyles = xcss({
	marginTop: 'space.300',
});
